import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import type { CrmMessageResponse, UnifiedContactResponse, LinkableContactResponse } from "../../../types/crm";
import {
  getContactById,
  sendContactMessage,
  sendContactMessageAttachment,
  deleteCrmContact,
  getLinkableContacts,
} from "../../../services/crmContacts";
import { checkContactSlackWorkspaceStatus, getConnectors } from "../../../services/crmConnectors";
import { SlackConvoModal } from "./SlackConvoModal";
import { EditContactModal } from "./EditContactModal";
import type { UnifiedContactResponse as UCR } from "../../../types/crm";
import { getContactMessages, markChannelRead } from "../../../services/crmMessages";
import { formatPhoneNumber } from "../../../utils/formatters";
import { pipelineStageLabel } from "../../../constants/pipelineStages";
import { ContactMessageTimeline } from "../messages/ContactMessageTimeline";
import { PlayerInteractionLog } from "./PlayerInteractionLog";
import { PlayerMediaSection } from "./PlayerMediaSection";
import { MediaBrowserModal } from "./MediaBrowserModal";
import { ComposeXPostModal } from "./ComposeXPostModal";
import { XPostsSection } from "./XPostsSection";
import type { CrmMediaResponse } from "../../../types/crm";
import "./ContactDetailView.css";

const TYPE_LABELS: Record<string, string> = {
  Coach: "Coach",
  Parent: "Parent",
  ProspectProfile: "Player",
};

const ALL_CHANNELS = [
  { key: "X", label: "X (Twitter)" },
  { key: "Slack", label: "Slack" },
  { key: "Gmail", label: "Gmail" },
  { key: "BlueBubbles", label: "SMS / iMessage" },
];

// Display-only mapping for PreferredContactMethod values. The DB still stores
// "Quo" as the legacy enum, but it now means "phone-preferred" with BlueBubbles
// as the delivery provider. Recruiters should never see "Quo" in the UI.
const PREFERENCE_LABELS: Record<string, string> = {
  Gmail: "Email",
  Quo: "Phone",
  X: "X",
};

function getChannelsForContact(contactType: string) {
  // Slack is only for players (ProspectProfile) and parents
  if (contactType === "Coach") return ALL_CHANNELS.filter((c) => c.key !== "Slack");
  return ALL_CHANNELS;
}

function getChannelSendDisabledReason(
  channel: string,
  contact: UnifiedContactResponse,
  bbConnectorActive: boolean | null
): string | null {
  switch (channel) {
    case "X":           return contact.xHandle      ? null : "No X handle on file";
    case "Slack":       return contact.slackChannelId ? null : "No channel set up — use New Conversation to create one";
    case "Gmail":       return contact.email         ? null : "No email address on file";
    case "BlueBubbles":
      if (bbConnectorActive === false) return "BlueBubbles is not connected";
      if (!contact.phone) return "Add a phone number to message this contact on SMS / iMessage";
      return null;
    default:            return null;
  }
}

const BB_ALLOWED_IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
]);

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

const AVATAR_COLORS: Record<string, string> = {
  Coach: "#1E3B73",
  ProspectProfile: "#15803d",
  Parent: "#92400e",
};

type SlackWorkspaceStatus = 'loading' | 'in_workspace' | 'not_in_workspace' | 'no_username' | null;

export const ContactDetailView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const channelParam = searchParams.get("channel");

  const [contact, setContact] = useState<UnifiedContactResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeChannel, setActiveChannel] = useState<string | null>(null);
  const [composeText, setComposeText] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [attachedMedia, setAttachedMedia] = useState<CrmMediaResponse | null>(null);
  const [showMediaBrowser, setShowMediaBrowser] = useState(false);
  const [showNewConvo, setShowNewConvo] = useState(false);
  const [slackWorkspaceStatus, setSlackWorkspaceStatus] = useState<SlackWorkspaceStatus>(null);
  const [pendingMessages, setPendingMessages] = useState<CrmMessageResponse[]>([]);
  const [timelineRefreshKey, setTimelineRefreshKey] = useState(0);
  const [linkableContacts, setLinkableContacts] = useState<LinkableContactResponse[]>([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showComposeXPost, setShowComposeXPost] = useState(false);
  const [xPostRefreshKey, setXPostRefreshKey] = useState(0);
  // null = unknown (still loading); true/false = whether the user has an active BB connector
  const [bbConnectorActive, setBbConnectorActive] = useState<boolean | null>(null);
  const [bbAttachedFile, setBbAttachedFile] = useState<File | null>(null);
  const [bbAttachedPreview, setBbAttachedPreview] = useState<string | null>(null);
  const [bbAttachError, setBbAttachError] = useState("");

  useEffect(() => {
    let cancelled = false;
    getConnectors()
      .then((connectors) => {
        if (cancelled) return;
        const hasActiveBB = connectors.some(
          (c) => c.connectorType === "BlueBubbles" && c.status === "Active"
        );
        setBbConnectorActive(hasActiveBB);
      })
      .catch(() => {
        if (!cancelled) setBbConnectorActive(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Revoke object URLs when the attached file changes or unmounts so we don't leak.
  useEffect(() => {
    return () => {
      if (bbAttachedPreview) URL.revokeObjectURL(bbAttachedPreview);
    };
  }, [bbAttachedPreview]);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getContactById(id)
      .then(async (found) => {
        setContact(found);
        const channels = getChannelsForContact(found.contactType);
        // Load linkable contacts (parents for players, players for parents)
        if (found.contactType === "ProspectProfile" || found.contactType === "Parent") {
          getLinkableContacts(found.contactType, found.id)
            .then(setLinkableContacts)
            .catch(() => {/* non-critical */});
        }
        // Proactively check Slack workspace membership for prospects and parents
        if (found.contactType === "ProspectProfile" || found.contactType === "Parent") {
          if (!found.slackUsername) {
            setSlackWorkspaceStatus('no_username');
          } else {
            setSlackWorkspaceStatus('loading');
            checkContactSlackWorkspaceStatus(found.contactType, found.id)
              .then(({ isInWorkspace }) =>
                setSlackWorkspaceStatus(isInWorkspace ? 'in_workspace' : 'not_in_workspace')
              )
              .catch(() => setSlackWorkspaceStatus('not_in_workspace'));
          }
        }
        // Normalize "twitter" → "x" for channel matching
        const normalizeChannel = (ch: string) => {
          const lower = ch.toLowerCase();
          return lower === "twitter" ? "x" : lower;
        };
        // If a channel was specified via query param, honour it and mark as read
        if (channelParam) {
          const matched = channels.find(
            (ch) => normalizeChannel(ch.key) === normalizeChannel(channelParam)
          );
          const resolvedChannel = matched?.key ?? channels[0].key;
          setActiveChannel(resolvedChannel);
          markChannelRead(found.contactType, found.id, resolvedChannel).catch(() => {});
          return;
        }
        // Otherwise, determine the most-recently-active channel
        try {
          const msgData = await getContactMessages(found.contactType, found.id, 1, 50);
          if (msgData.items.length === 0) {
            setActiveChannel(channels[0].key);
            markChannelRead(found.contactType, found.id, channels[0].key).catch(() => {});
            return;
          }
          const mostRecentChannel = msgData.items[0].channel;
          const matched = channels.find(
            (ch) => normalizeChannel(ch.key) === normalizeChannel(mostRecentChannel)
          );
          const resolvedChannel = matched?.key ?? channels[0].key;
          setActiveChannel(resolvedChannel);
          markChannelRead(found.contactType, found.id, resolvedChannel).catch(() => {});
        } catch {
          setActiveChannel(channels[0].key);
          markChannelRead(found.contactType, found.id, channels[0].key).catch(() => {});
        }
      })
      .catch(() => setError("Failed to load contact."))
      .finally(() => setLoading(false));
  }, [id]);

  const handleBbFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file after remove
    if (!file) return;
    if (!BB_ALLOWED_IMAGE_TYPES.has(file.type)) {
      setBbAttachError('Only JPEG, PNG, GIF, or WebP images are supported.');
      return;
    }
    setBbAttachError('');
    if (bbAttachedPreview) URL.revokeObjectURL(bbAttachedPreview);
    setBbAttachedFile(file);
    setBbAttachedPreview(URL.createObjectURL(file));
  };

  const handleBbFileRemove = () => {
    if (bbAttachedPreview) URL.revokeObjectURL(bbAttachedPreview);
    setBbAttachedFile(null);
    setBbAttachedPreview(null);
    setBbAttachError('');
  };

  const handleSend = async () => {
    if (!contact || !activeChannel) return;

    const isBbWithFile = activeChannel === 'BlueBubbles' && bbAttachedFile != null;
    if (!composeText.trim() && !isBbWithFile) return;

    const optimistic: CrmMessageResponse = {
      id: `pending-${Date.now()}`,
      connectorId: '',
      connectorType: activeChannel,
      contactId: contact.id,
      contactType: contact.contactType,
      channel: activeChannel,
      direction: 'Outbound',
      body: composeText.trim(),
      status: 'Pending',
      occurredAt: new Date().toISOString(),
      ingestedAt: new Date().toISOString(),
    };

    setPendingMessages((prev) => [...prev, optimistic]);
    const sentBody = optimistic.body ?? '';
    const sentFile = bbAttachedFile;
    setComposeText('');
    setSending(true);
    setSendError('');

    try {
      const result = isBbWithFile && sentFile
        ? await sendContactMessageAttachment(contact.contactType, contact.id, {
            channel: 'BlueBubbles',
            body: sentBody,
            file: sentFile,
          })
        : await sendContactMessage(contact.contactType, contact.id, {
            channel: activeChannel,
            body: sentBody,
            crmMediaId: attachedMedia?.id,
          });
      setAttachedMedia(null);
      if (isBbWithFile) handleBbFileRemove();
      if (result.success) {
        setPendingMessages([]);
        setTimelineRefreshKey((k) => k + 1);
      } else {
        setPendingMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
        setSendError(result.error ?? 'Failed to send message.');
      }
    } catch {
      setPendingMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
      setSendError('Failed to send message.');
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async () => {
    if (!contact) return;
    if (!window.confirm(`Delete ${contact.displayName}? This cannot be undone.`)) return;
    try {
      await deleteCrmContact(contact.contactType, contact.id);
      navigate(`/crm/contacts?type=${contact.contactType}`);
    } catch {
      setError("Failed to delete contact.");
    }
  };

  const handleBack = () => {
    const state = location.state as { from?: string; fromLabel?: string } | null;
    if (state?.from) {
      navigate(state.from);
      return;
    }
    const type = contact?.contactType ?? "Coach";
    navigate(`/crm/contacts?type=${type}`);
  };

  const backLabel = () => {
    const fromLabel = (location.state as { fromLabel?: string } | null)?.fromLabel;
    if (fromLabel) return fromLabel;
    const type = contact?.contactType ?? "Coach";
    return TYPE_LABELS[type] ? `${TYPE_LABELS[type]}s` : "Contacts";
  };

  if (loading) {
    return <div className="contact-detail-view__loading">Loading...</div>;
  }

  if (error || !contact) {
    return (
      <div className="contact-detail-view__error">
        {error || "Contact not found."}
        <button className="contact-detail-view__back-link" onClick={handleBack}>
          ← Back
        </button>
      </div>
    );
  }

  const avatarColor = AVATAR_COLORS[contact.contactType] ?? "#1E3B73";
  const channelDisabledReason =
    contact && activeChannel
      ? getChannelSendDisabledReason(activeChannel, contact, bbConnectorActive)
      : null;

  const firstName = contact.displayName.split(' ')[0];
  const missingContactMessage = (() => {
    if (channelDisabledReason && activeChannel !== 'Slack')
      return `Add ${activeChannel} contact info to communicate with ${firstName}`;
    if (activeChannel === 'Slack' && slackWorkspaceStatus === 'no_username')
      return `Add Slack contact info to communicate with ${firstName}`;
    if (activeChannel === 'Slack' && slackWorkspaceStatus === 'not_in_workspace')
      return `Invite this user to your Slack workspace to communicate with them`;
    if (activeChannel === 'Slack' && slackWorkspaceStatus === 'in_workspace' && !contact.slackChannelId)
      return `No channel set up — use New Conversation to create one`;
    return null;
  })();

  return (
    <div className="contact-detail-view">
      <div className="contact-detail-view__breadcrumb">
        <button className="contact-detail-view__back-link" onClick={handleBack}>
          ← Back to {backLabel()}
        </button>
      </div>

      <div className="contact-detail-view__card">
        <div className="contact-detail-view__card-top">
          <div className="contact-detail-view__card-left">
            <div
              className="contact-detail-view__avatar"
              style={{ backgroundColor: avatarColor }}
            >
              {getInitials(contact.displayName)}
            </div>
            <div className="contact-detail-view__info">
              <div className="contact-detail-view__name-row">
                <h1 className="contact-detail-view__name">{contact.displayName}</h1>
                <span className={`contact-detail-view__badge contact-detail-view__badge--${contact.contactType.toLowerCase()}`}>
                  {TYPE_LABELS[contact.contactType] ?? contact.contactType}
                </span>
                {contact.pipelineStage && (
                  <span className="contact-detail-view__stage-badge">
                    {pipelineStageLabel(contact.pipelineStage)}
                  </span>
                )}
              </div>
              {(contact.school || contact.division || contact.state) && (
                <div className="contact-detail-view__meta-line">
                  {[contact.school, contact.division, contact.state].filter(Boolean).join(" · ")}
                </div>
              )}
            </div>
          </div>
          <div className="contact-detail-view__actions">
            {contact.contactType === "ProspectProfile" && (
              <button
                className="contact-detail-view__xpost-btn"
                onClick={() => setShowComposeXPost(true)}
              >
                Post to X
              </button>
            )}
            <button
              className="contact-detail-view__edit-btn"
              onClick={() => setShowEditModal(true)}
            >
              Edit
            </button>
            <button
              className="contact-detail-view__delete-btn"
              onClick={handleDelete}
            >
              Delete
            </button>
          </div>
        </div>

        {/* Expanded detail fields for ProspectProfile */}
        {contact.contactType === "ProspectProfile" && (
          <div className="contact-detail-view__details">
            <div className="contact-detail-view__details-section">
              <div className="contact-detail-view__details-heading">Contact</div>
              <div className="contact-detail-view__details-grid">
                <DetailItem label="Email" value={contact.email} href={contact.email ? `mailto:${contact.email}` : undefined} />
                <DetailItem label="Phone" value={contact.phone ? formatPhoneNumber(contact.phone) : undefined} href={contact.phone ? `tel:${contact.phone}` : undefined} />
                <DetailItem label="X (Twitter)" value={contact.xHandle ? `@${contact.xHandle.replace(/^@/, '')}` : undefined} />
                <DetailItem label="Slack" value={contact.slackUsername || undefined} />
              </div>
            </div>

            <div className="contact-detail-view__details-section">
              <div className="contact-detail-view__details-heading">Player Details</div>
              <div className="contact-detail-view__details-grid">
                <DetailItem label="Position" value={contact.position} />
                <DetailItem label="Class" value={contact.gradYear?.toString()} />
                <DetailItem label="Bats / Throws" value={contact.bats || contact.throws ? `${contact.bats || '—'} / ${contact.throws || '—'}` : undefined} />
                <DetailItem label="Height" value={contact.height} />
                <DetailItem label="Weight" value={contact.weight ? `${contact.weight} lbs` : undefined} />
                <DetailItem label="Projected Division" value={contact.projectedDivisions?.length ? contact.projectedDivisions.join(", ") : undefined} />
              </div>
            </div>

            <div className="contact-detail-view__details-section">
              <div className="contact-detail-view__details-heading">Academic & Location</div>
              <div className="contact-detail-view__details-grid">
                <DetailItem label="School" value={contact.school} />
                <DetailItem label="Location" value={contact.city || contact.state ? [contact.city, contact.state].filter(Boolean).join(", ") : undefined} />
                <DetailItem label="GPA" value={contact.gpa} />
                <DetailItem label="Intended Major" value={contact.intendedMajor} />
                <DetailItem label="Preferred Locations" value={contact.preferredLocations?.length ? contact.preferredLocations.join(", ") : undefined} />
              </div>
            </div>

            {(contact.travelTeam || contact.travelTeamCoachName || contact.travelTeamCoachPhone) && (
              <div className="contact-detail-view__details-section">
                <div className="contact-detail-view__details-heading">Travel Team</div>
                <div className="contact-detail-view__details-grid">
                  <DetailItem label="Team" value={contact.travelTeam} />
                  <DetailItem label="Coach" value={contact.travelTeamCoachName} />
                  <DetailItem label="Coach Phone" value={contact.travelTeamCoachPhone ? formatPhoneNumber(contact.travelTeamCoachPhone) : undefined} />
                </div>
              </div>
            )}

            {(contact.videoLink || contact.scoutingProfileLink) && (
              <div className="contact-detail-view__details-section">
                <div className="contact-detail-view__details-heading">Links</div>
                <div className="contact-detail-view__details-grid">
                  {contact.videoLink && (
                    <DetailItem label="Video" value="View" href={contact.videoLink} external />
                  )}
                  {contact.scoutingProfileLink && (
                    <DetailItem label="Scouting Profile" value="View" href={contact.scoutingProfileLink} external />
                  )}
                </div>
              </div>
            )}

            <div className="contact-detail-view__details-section">
              <div className="contact-detail-view__details-heading">Recruiting</div>
              <div className="contact-detail-view__details-grid">
                <DetailItem label="Recruitment Stage" value={contact.recruitmentStage} />
                <DetailItem label="End Goal" value={contact.endGoal} />
              </div>
            </div>

            <div className="contact-detail-view__details-section">
              <div className="contact-detail-view__details-heading">Notes</div>
              <p className={`contact-detail-view__notes${!contact.notes ? " contact-detail-view__detail-value--empty" : ""}`}>
                {contact.notes || "\u2014"}
              </p>
            </div>

          </div>
        )}

        {/* Coach expanded details */}
        {contact.contactType === "Coach" && (
          <div className="contact-detail-view__details">
            <div className="contact-detail-view__details-section">
              <div className="contact-detail-view__details-heading">Contact</div>
              <div className="contact-detail-view__details-grid">
                <DetailItem label="Email" value={contact.email} href={contact.email ? `mailto:${contact.email}` : undefined} />
                <DetailItem label="Phone" value={contact.phone ? formatPhoneNumber(contact.phone) : undefined} href={contact.phone ? `tel:${contact.phone}` : undefined} />
                <DetailItem label="X (Twitter)" value={contact.xHandle ? `@${contact.xHandle.replace(/^@/, '')}` : undefined} />
                <DetailItem label="Preferred Contact" value={contact.preferredContactMethod ? (PREFERENCE_LABELS[contact.preferredContactMethod] ?? contact.preferredContactMethod) : undefined} />
              </div>
            </div>

            <div className="contact-detail-view__details-section">
              <div className="contact-detail-view__details-heading">Role</div>
              <div className="contact-detail-view__details-grid">
                <DetailItem label="Title" value={contact.roleTitle} />
                <DetailItem label="School / Team" value={contact.crmTeamName} />
              </div>
            </div>

            {(contact.crmTeamName || contact.division || contact.state || contact.city || contact.conference || contact.region) && (
              <div className="contact-detail-view__details-section">
                <div className="contact-detail-view__details-heading">School Details</div>
                <div className="contact-detail-view__details-grid">
                  <DetailItem label="Division" value={contact.division} />
                  <DetailItem label="Conference" value={contact.conference} />
                  <DetailItem label="Region" value={contact.region} />
                  <DetailItem label="Location" value={contact.city || contact.state ? [contact.city, contact.state].filter(Boolean).join(", ") : undefined} />
                  {contact.teamTwitter && (
                    <DetailItem label="School Twitter" value={`@${contact.teamTwitter.replace(/^@/, '')}`} />
                  )}
                </div>
              </div>
            )}

            <div className="contact-detail-view__details-section">
              <div className="contact-detail-view__details-heading">Notes</div>
              <p className={`contact-detail-view__notes${!contact.notes ? " contact-detail-view__detail-value--empty" : ""}`}>
                {contact.notes || "\u2014"}
              </p>
            </div>
          </div>
        )}

        {/* Parent: basic contact info */}
        {contact.contactType === "Parent" && (
          <div className="contact-detail-view__details">
            <div className="contact-detail-view__details-grid">
              <DetailItem label="Email" value={contact.email} href={contact.email ? `mailto:${contact.email}` : undefined} />
              <DetailItem label="Phone" value={contact.phone ? formatPhoneNumber(contact.phone) : undefined} href={contact.phone ? `tel:${contact.phone}` : undefined} />
              <DetailItem label="X (Twitter)" value={contact.xHandle ? `@${contact.xHandle.replace(/^@/, '')}` : undefined} />
              <DetailItem label="Slack" value={contact.slackUsername || undefined} />
            </div>
            <div className="contact-detail-view__details-section">
              <div className="contact-detail-view__details-heading">Notes</div>
              <p className={`contact-detail-view__notes${!contact.notes ? " contact-detail-view__detail-value--empty" : ""}`}>
                {contact.notes || "\u2014"}
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="contact-detail-view__messages-section">
        <div className="contact-detail-view__section-header">
          <h2 className="contact-detail-view__section-title">Messages</h2>
          {activeChannel === "Slack" && contact.contactType === "ProspectProfile" && (
            slackWorkspaceStatus === 'loading' ? (
              <button className="contact-detail-view__new-convo-btn" disabled>
                Checking workspace...
              </button>
            ) : slackWorkspaceStatus === 'in_workspace' && !contact.slackChannelId ? (
              <button
                className="contact-detail-view__new-convo-btn"
                onClick={() => setShowNewConvo(true)}
              >
                + New Conversation
              </button>
            ) : null
          )}
        </div>

        {activeChannel === null ? (
          <div className="contact-detail-view__timeline">
            <div className="message-timeline__loading">Loading messages...</div>
          </div>
        ) : (
          <>
            <div className="contact-detail-view__channel-tabs">
              {getChannelsForContact(contact.contactType).map((ch) => (
                <button
                  key={ch.key}
                  className={`contact-detail-view__channel-tab ${activeChannel === ch.key ? "contact-detail-view__channel-tab--active" : ""}`}
                  onClick={() => {
                    setActiveChannel(ch.key);
                    markChannelRead(contact.contactType, contact.id, ch.key).catch(() => {});
                  }}
                >
                  {ch.label}
                </button>
              ))}
            </div>

            <div className="contact-detail-view__timeline">
              <ContactMessageTimeline
                contactType={contact.contactType}
                contactId={contact.id}
                channel={activeChannel}
                refreshKey={timelineRefreshKey}
                pendingMessages={pendingMessages}
                missingContactMessage={missingContactMessage ?? undefined}
              />
            </div>

            <div className="contact-detail-view__compose">
              <textarea
                className="contact-detail-view__compose-input"
                placeholder={`Send via ${getChannelsForContact(contact.contactType).find((c) => c.key === activeChannel)?.label ?? activeChannel}...`}
                value={composeText}
                onChange={(e) => setComposeText(e.target.value)}
                rows={3}
              />
              {contact.contactType === "ProspectProfile" && activeChannel !== "BlueBubbles" && (
                <div className="contact-detail-view__compose-media-row">
                  {attachedMedia ? (
                    <div className="contact-detail-view__attached-media">
                      {attachedMedia.thumbnailUrl ? (
                        <img src={attachedMedia.thumbnailUrl} alt="" className="contact-detail-view__attached-thumb" />
                      ) : (
                        <span className="contact-detail-view__attached-icon">{attachedMedia.mediaType === "Video" ? "🎬" : "🖼"}</span>
                      )}
                      <span className="contact-detail-view__attached-name">
                        {attachedMedia.originalFileName || `${attachedMedia.mediaType}`}
                      </span>
                      <button
                        className="contact-detail-view__attached-remove"
                        onClick={() => setAttachedMedia(null)}
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <button
                      className="contact-detail-view__attach-media-btn"
                      type="button"
                      onClick={() => setShowMediaBrowser(true)}
                    >
                      Attach Media
                    </button>
                  )}
                </div>
              )}
              {activeChannel === "BlueBubbles" && (
                <div className="contact-detail-view__compose-media-row">
                  {bbAttachedFile ? (
                    <div className="contact-detail-view__attached-media">
                      {bbAttachedPreview ? (
                        <img src={bbAttachedPreview} alt="" className="contact-detail-view__attached-thumb" />
                      ) : (
                        <span className="contact-detail-view__attached-icon">🖼</span>
                      )}
                      <span className="contact-detail-view__attached-name">
                        {bbAttachedFile.name}
                      </span>
                      <button
                        className="contact-detail-view__attached-remove"
                        onClick={handleBbFileRemove}
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <label className="contact-detail-view__attach-media-btn">
                      Attach Image
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={handleBbFileSelect}
                        style={{ display: 'none' }}
                      />
                    </label>
                  )}
                </div>
              )}
              {bbAttachError && <div className="contact-detail-view__send-error">{bbAttachError}</div>}
              {channelDisabledReason && (activeChannel === 'Slack' || activeChannel === 'BlueBubbles') && !missingContactMessage && (
                <div className="contact-detail-view__channel-hint">
                  {channelDisabledReason}
                </div>
              )}
              {sendError && <div className="contact-detail-view__send-error">{sendError}</div>}
              <button
                className="contact-detail-view__compose-send"
                disabled={
                  (!composeText.trim() && !(activeChannel === 'BlueBubbles' && bbAttachedFile)) ||
                  sending ||
                  channelDisabledReason !== null
                }
                onClick={handleSend}
              >
                {sending ? "Sending..." : "Send"}
              </button>
            </div>
          </>
        )}
      </div>
      {showNewConvo && activeChannel === "Slack" && contact.contactType === "ProspectProfile" && (
        <SlackConvoModal
          isOpen={showNewConvo}
          prospectProfileId={contact.id}
          linkedParents={linkableContacts.filter((c) => c.contactType === "Parent")}
          onClose={() => setShowNewConvo(false)}
          onSuccess={() => {
            setTimelineRefreshKey((k) => k + 1);
            // Re-fetch contact to pick up the new slackChannelId
            getContactById(contact.id).then(setContact).catch(() => {});
            setShowNewConvo(false);
          }}
        />
      )}
      {contact.contactType === "ProspectProfile" && (
        <PlayerMediaSection profileId={contact.id} />
      )}
      {contact.contactType === "ProspectProfile" && (
        <XPostsSection profileId={contact.id} refreshKey={xPostRefreshKey} />
      )}
      {contact.contactType === "ProspectProfile" && (
        <PlayerInteractionLog profileId={contact.id} />
      )}
      {contact.contactType === "ProspectProfile" && (
        <MediaBrowserModal
          isOpen={showMediaBrowser}
          crmProspectProfileId={contact.id}
          onSelect={(media) => {
            setAttachedMedia(media);
            setShowMediaBrowser(false);
          }}
          onClose={() => setShowMediaBrowser(false)}
        />
      )}
      {showComposeXPost && contact.contactType === "ProspectProfile" && (
        <ComposeXPostModal
          isOpen={showComposeXPost}
          crmProspectProfileId={contact.id}
          onClose={() => setShowComposeXPost(false)}
          onSuccess={() => {
            setShowComposeXPost(false);
            setXPostRefreshKey((k) => k + 1);
          }}
        />
      )}
      {showEditModal && contact && (
        <EditContactModal
          isOpen={showEditModal}
          contact={contact}
          onClose={() => setShowEditModal(false)}
          onSuccess={(updated: UCR) => {
            setContact(updated);
            setShowEditModal(false);
          }}
        />
      )}
    </div>
  );
};

function DetailItem({ label, value, href, external }: {
  label: string;
  value?: string | null;
  href?: string;
  external?: boolean;
}) {
  if (!value) {
    return (
      <div className="contact-detail-view__detail-item">
        <span className="contact-detail-view__detail-label">{label}</span>
        <span className="contact-detail-view__detail-value contact-detail-view__detail-value--empty">—</span>
      </div>
    );
  }

  return (
    <div className="contact-detail-view__detail-item">
      <span className="contact-detail-view__detail-label">{label}</span>
      {href ? (
        <a
          className="contact-detail-view__detail-value contact-detail-view__detail-link"
          href={href}
          {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
        >
          {value}
        </a>
      ) : (
        <span className="contact-detail-view__detail-value">{value}</span>
      )}
    </div>
  );
}
